import React, { useEffect, useRef, useState } from 'react';
import { Button, Progress, Space, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { useEditorDoc, useEditorDispatch, useEditorMeta, useEditorUi } from '../../EditorProvider';
import type { RebusDoc } from './doc';
import type { RebusAction } from './reducer';
import { ACCEPTED_MIME, uploadImage } from './upload';
import type { RebusUi } from './ui';

// O controle de subir imagem. Vive em DOIS lugares (o empty state do Canvas e o
// formulário do Inspector) — por isso é componente próprio, para não duplicar
// marcação e lógica.
//
// ⚠️ Extrair o componente NÃO compartilha estado: cada mount tem seu PRÓPRIO
// `percent`/`erro` (useState local). Se um upload começa pelo Canvas, a instância do
// Inspector não sabe nada sobre ele — sem mais nada, ela aceitaria iniciar um SEGUNDO
// upload concorrente para o mesmo `idJogo` (dois `uploadImage` correndo ao mesmo
// tempo; ganha quem resolver por último, e o outro vira objeto órfão no S3).
//
// A coordenação de verdade é `RebusUi.uploading` (ver `ui.ts`), guardado no
// MetaContext do EditorProvider — o mesmo contexto que Canvas e Inspector já
// compartilham por serem irmãos dentro do mesmo provider (mesmo mecanismo que o
// caça-palavras usa em `wordsearch/ui.ts`). Enquanto ele estiver `true`, o outro
// ponto fica com o botão desabilitado E recusa iniciar mesmo que o `change` chegue
// direto no `<input>` escondido (ver o guard no início de `escolher`).
//
// O `<input type="file">` fica escondido atrás do Button (o input nativo não aceita
// estilo e o antd faz o mesmo por dentro). Quem é anunciado ao leitor de tela é o
// Button — o input está em `display: none` e some da árvore de acessibilidade; o
// `aria-label` dele existe para o TESTE ter como escolher o arquivo.

interface Props {
  /** Botão ocupando a largura toda (uso do Inspector). */
  block?: boolean;
}

export const ImageUploader: React.FC<Props> = ({ block }) => {
  const doc = useEditorDoc<RebusDoc>();
  const { do: dispatchAction } = useEditorDispatch();
  const { editavel } = useEditorMeta();
  const [ui, setUi] = useEditorUi<RebusUi>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const dispatch = (action: RebusAction) => dispatchAction(action);
  const enviando = percent !== null;
  const semJogo = doc.idJogo == null;
  // `ui.uploading` é `true` tanto para o ponto que está subindo (este) quanto para o
  // outro — o que interessa aqui é saber se o OUTRO ponto está ocupado.
  const ocupadoPeloOutro = ui.uploading && !enviando;

  // O upload continua correndo se o autor sair da tela (ou se o Canvas trocar o
  // empty state pela imagem) — sem esta guarda, o callback de progresso do axios
  // tenta atualizar um componente desmontado. Mesmo padrão do `alive` do EditorShell.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const escolher = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Zera o input: sem isso, escolher o MESMO arquivo depois de um erro não dispara
    // `change` de novo e o botão parece morto.
    event.target.value = '';
    const idJogo = doc.idJogo;
    if (!file || idJogo == null) return;

    // Segunda trava, além do `disabled` do botão: o `<input>` escondido não tem
    // `disabled` (só o Button tem — ver o comentário no topo do arquivo), então um
    // `change` disparado direto nele (um clique físico entre o render e o desabilitar,
    // ou um teste que contorna o botão) não pode iniciar um segundo upload.
    if (ui.uploading) return;

    setErro(null);
    setPercent(0);
    setUi((prev) => ({ ...prev, uploading: true }));
    uploadImage(idJogo, file, (pct) => aliveRef.current && setPercent(pct)).then(
      (url) => {
        if (!aliveRef.current) return;
        // Zera progresso e trava ANTES de despachar: o dispatch pode desmontar este
        // componente (no Canvas o empty state dá lugar à imagem).
        setPercent(null);
        setUi((prev) => ({ ...prev, uploading: false }));
        dispatch({ type: 'SET_IMAGEM', imagem: url });
      },
      (err: unknown) => {
        if (!aliveRef.current) return;
        setPercent(null);
        setUi((prev) => ({ ...prev, uploading: false }));
        setErro(err instanceof Error ? err.message : 'Falha ao subir a imagem.');
      },
    );
  };

  return (
    <Space direction="vertical" size={4} style={{ width: block ? '100%' : undefined }}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MIME.join(',')}
        aria-label="Arquivo da imagem"
        style={{ display: 'none' }}
        onChange={escolher}
      />
      <Button
        block={block}
        icon={<UploadOutlined />}
        loading={enviando}
        disabled={!editavel || semJogo || enviando || ocupadoPeloOutro}
        onClick={() => inputRef.current?.click()}
      >
        {doc.imagem ? 'Trocar imagem' : 'Subir imagem'}
      </Button>

      {semJogo && (
        <Typography.Text type="secondary">
          Salve primeiro: a imagem é enviada para o jogo já criado.
        </Typography.Text>
      )}

      {ocupadoPeloOutro && (
        <Typography.Text type="secondary">
          Aguarde: já há um upload em andamento.
        </Typography.Text>
      )}

      {enviando && <Progress percent={percent ?? 0} size="small" />}

      {erro && <Typography.Text type="danger">{erro}</Typography.Text>}
    </Space>
  );
};
